import {
  AccountId,
  LedgerEntryId,
  MoneyAmount,
  SubWalletId,
  TransactionId,
  TransactionKind,
  WalletFreezeActor,
  WalletId,
  WalletStatus,
} from "@ggcoin/core";
import { z } from "zod";

/**
 * Posted-transaction event — published right after `PostTransactionService`
 * succeeds at the api edge. Carries just enough for the SSE client to render
 * a feed row without making a second round trip.
 *
 * `entries` are present for callers that want to render the splits inline;
 * a UI that only shows the header can ignore the field.
 */
export const TransactionPostedEvent = z.object({
  type: z.literal("transaction.posted"),
  accountId: AccountId,
  transactionId: TransactionId,
  kind: TransactionKind,
  // Narrow to the literal; a `posted` event with any other status would be a
  // contract bug, not a wire-format variant the consumer should switch on.
  status: z.literal("posted"),
  amount: MoneyAmount,
  postedAt: z.coerce.date(),
  entries: z.array(
    z.object({
      id: LedgerEntryId,
      walletId: WalletId,
      subWalletId: SubWalletId.nullable(),
      direction: z.enum(["debit", "credit"]),
      amount: MoneyAmount,
    }),
  ),
});
export type TransactionPostedEvent = z.output<typeof TransactionPostedEvent>;

/** Reversal event — original is now `reversed`; reversal tx is `posted`. */
export const TransactionReversedEvent = z.object({
  type: z.literal("transaction.reversed"),
  accountId: AccountId,
  originalTransactionId: TransactionId,
  reversalTransactionId: TransactionId,
  amount: MoneyAmount,
  postedAt: z.coerce.date(),
});
export type TransactionReversedEvent = z.output<typeof TransactionReversedEvent>;

/** Union of every event the `tx:` channel ever carries. */
export const TransactionEvent = z.discriminatedUnion("type", [
  TransactionPostedEvent,
  TransactionReversedEvent,
]);
export type TransactionEvent = z.output<typeof TransactionEvent>;

/**
 * Wallet-balance change event — the running cache moved. Lets a connected
 * wallet UI update its displayed balance without polling.
 */
export const WalletBalanceChangedEvent = z.object({
  type: z.literal("wallet.balance-changed"),
  accountId: AccountId,
  walletId: WalletId,
  subWalletId: SubWalletId.nullable(),
  /** New cached balance after the change. Can be negative (signed delta). */
  balance: z.bigint(),
  /** Transaction id that triggered the change, for cross-tab dedupe. */
  transactionId: TransactionId,
});
export type WalletBalanceChangedEvent = z.output<typeof WalletBalanceChangedEvent>;

/**
 * Approval-required push — `AuthorizeSpendService` returned `require-approval`
 * and the api edge surfaces it to the master so a phone can OK or deny.
 */
export const ApprovalRequestedEvent = z.object({
  type: z.literal("wallet.approval-requested"),
  accountId: AccountId,
  masterWalletId: WalletId,
  subWalletId: SubWalletId,
  amount: MoneyAmount,
  /** Caller-supplied opaque id so a phone can ack the specific request. */
  approvalRequestId: z.string().min(1),
});
export type ApprovalRequestedEvent = z.output<typeof ApprovalRequestedEvent>;

/**
 * Wallet status-change push (#153) — a wallet was frozen or unfrozen (by the
 * owner or an operator). Lets a connected wallet UI reflect the freeze and gate
 * spend actions live, without a manual refresh. `frozenBy` tells the UI whether
 * the owner may self-unfreeze (operator freezes are sticky).
 */
export const WalletStatusChangedEvent = z.object({
  type: z.literal("wallet.status-changed"),
  accountId: AccountId,
  walletId: WalletId,
  status: WalletStatus,
  frozenBy: WalletFreezeActor.nullable(),
});
export type WalletStatusChangedEvent = z.output<typeof WalletStatusChangedEvent>;

/** Union of every event the `wallet:` channel ever carries. */
export const WalletEvent = z.discriminatedUnion("type", [
  WalletBalanceChangedEvent,
  ApprovalRequestedEvent,
  WalletStatusChangedEvent,
]);
export type WalletEvent = z.output<typeof WalletEvent>;

/** Top-level union — useful when the publisher hands one channel two shapes. */
export const RealtimeEvent = z.union([TransactionEvent, WalletEvent]);
export type RealtimeEvent = z.output<typeof RealtimeEvent>;
